import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ISelectOption, SelectMini } from '@salecycle/ui-component-library';
import { useTemplateEditorState } from '../context/UseTemplateEditorContext';
import { useCallback, useMemo, useState } from 'react';
import { SUPPORTED_LANGUAGES } from 'sections/client-management/ui/pages/client-settings/supported-languages/constants/SupportedLanguagesConstants';
import { TemplateWarningIcon } from './TemplateWarningIcon';

export interface TemplateLanguageSelectProps extends IntrinsicProps {
  onLanguageChange: (option: ISelectOption<any>) => void;
}

export const TemplateLanguageSelect = ({ onLanguageChange, ...props }) => {
  const { t } = useTranslation(['designs', 'supportedLanguages']);
  const { template, site } = useTemplateEditorState();
  const { language } = useParams<{ siteId: string; id: string; language: string }>();
  const [siteDefault] = useState(site.supportedLanguagesSettings?.defaultLanguage ?? '');

  const getLabelValue = useCallback(
    (lan: string): string => {
      const labelLanguage = SUPPORTED_LANGUAGES.find(l => l.id === lan);
      return lan === siteDefault
        ? `${t(labelLanguage.name)} (${t('designs:designGallery.templateEditor.nameToolbar.languages.default')})`
        : `${t(labelLanguage.name)}`;
    },
    [siteDefault, t],
  );

  const getTemplateLanguages = useMemo((): ISelectOption<string>[] => {
    return (template.supportedLanguages ?? []).map(lan => {
      return {
        value: lan.language,
        label: getLabelValue(lan.language),
      };
    });
  }, [getLabelValue, template.supportedLanguages]);

  const selectedValue = useMemo(
    () => ({
      value: language,
      label: getLabelValue(language),
    }),
    [getLabelValue, language],
  );

  const isLanguageSupported = useMemo(
    () => site.supportedLanguagesSettings?.supportedLanguages?.includes(selectedValue.value),
    [selectedValue.value, site.supportedLanguagesSettings?.supportedLanguages],
  );

  return (
    <>
      <SelectMini
        ariaLabel={t('designs:designGallery.templateEditor.nameToolbar.languages.label')}
        inputId="TemplateLanguageID"
        placeholder="test"
        options={getTemplateLanguages}
        onChange={onLanguageChange}
        value={selectedValue}
        {...props}
      />
      {!isLanguageSupported && <TemplateWarningIcon />}
    </>
  );
};
